import { useEffect, Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, ProtectedRoute } from './auth';

// Route-level code splitting: each page becomes its own JS chunk, loaded
// on demand. This keeps heavy, rarely-used pages (e.g. the admin analytics
// dashboard, which pulls in the recharts charting library) out of the
// initial bundle that every visitor downloads on first load.
const CataloguePage = lazy(() => import('./pages/CataloguePage'));
const PackageDetailPage = lazy(() => import('./pages/PackageDetailPage'));
const ReviewsPage = lazy(() => import('./pages/ReviewsPage'));
const RecipePage = lazy(() => import('./pages/RecipePage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const DisclosurePage = lazy(() => import('./pages/DisclosurePage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const AdminLoginPage = lazy(() => import('./pages/AdminLoginPage'));
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage'));
const AdminAnalyticsPage = lazy(() => import('./pages/AdminAnalyticsPage'));

function RouteLoadingFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-charcoal" role="status">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-gold border-t-transparent" />
      <span className="sr-only">Loading…</span>
    </div>
  );
}

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

function GA4RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    window.gtag?.('event', 'page_view', {
      page_path: location.pathname + location.search,
    });
  }, [location]);
  return null;
}

export default function App() {
  return (
    <HelmetProvider>
    <AuthProvider>
      <GA4RouteTracker />
      <Suspense fallback={<RouteLoadingFallback />}>
        <Routes>
          <Route path="/" element={<CataloguePage />} />
          <Route path="/packages/:id" element={<PackageDetailPage />} />
          <Route path="/packages/:id/reviews" element={<ReviewsPage />} />
          <Route path="/packages/:id/recipes/:courseId" element={<RecipePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/disclosure" element={<DisclosurePage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/admin" element={<Navigate to="/admin/login" replace />} />
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute>
                <AdminDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/analytics"
            element={
              <ProtectedRoute>
                <AdminAnalyticsPage />
              </ProtectedRoute>
            }
          />
        </Routes>
      </Suspense>
    </AuthProvider>
    </HelmetProvider>
  );
}
